/**
 * NIL Go readiness composite (US-033.1).
 *
 * Pure function — no DB, no network. Caller passes a snapshot of the deal
 * + its related entities; we return a typed checklist that drives both the
 * UI panel and the `submit` endpoint's "can this submit?" gate. Same idea
 * as `computeDeliverableReadiness` (US-030) but lifted to deal-level.
 *
 * The result groups checks by the editor they map to so the UI can render
 * "Fix it" links that take the athlete straight to the right surface
 * (US-028 payor editor / US-029 compensation editor / US-030 deliverable
 * editor / US-031 eligibility card / US-003 contract upload).
 *
 * Status meanings:
 *   - ready                — every required check passes. Submit button shows.
 *   - needs_attention      — one or more required checks fail. Submit hidden,
 *                            checklist surfaces what's wrong.
 *   - not_reportable       — total cash-equivalent for this deal is under
 *                            the $600 NIL Go reporting threshold AND
 *                            aggregate exposure for the payor (when known)
 *                            is also under $600. Submit hidden; an opt-in
 *                            "Submit anyway" appears in the UI.
 */
import { ACTIVITY_METADATA_SCHEMAS, type ActivityCategory } from '@app/validators';

export const NIL_GO_THRESHOLD_USD = 600;

export type ReadinessStatus = 'ready' | 'needs_attention' | 'not_reportable';

export type CheckId =
  | 'payor_present'
  | 'payor_validated'
  | 'payor_classified'
  | 'compensation_present'
  | 'compensation_positive_total'
  | 'activity_present'
  | 'activity_detailed'
  | 'athlete_ncaa_id'
  | 'athlete_eligibility_verified'
  | 'deal_terms_uploaded';

export interface ReadinessCheck {
  id: CheckId;
  ok: boolean;
  /** One-line plain-language description shown in the checklist. */
  message: string;
  /** Where the "Fix it" link should send the athlete. */
  fixHref?: string;
}

export interface ReadinessResult {
  status: ReadinessStatus;
  checks: ReadinessCheck[];
  /** Sum of cash-equivalent across this deal's compensation items. Used by
   * the UI to display the not-reportable threshold context. */
  dealTotalCashEquivalent: number;
  /** Aggregate exposure (CURRENT calendar year, this athlete × payor) when
   * the caller has it. null when unknown. Drives the YTD context line in
   * the panel description. */
  payorAggregateExposure: number | null;
  /** Deals with this payor — ACROSS EVERY YEAR — that crossed the $600
   * threshold in their own tax year and have not yet been submitted to
   * NIL Go. NIL Go obligations don't reset on Jan 1, so the banner uses
   * this list, not the calendar-year aggregate. Empty when payor is
   * unset or nothing is owed. */
  unsubmittedReportableWithPayor: Array<{
    id: string;
    title: string;
    anchorDate: string;
    anchorYear: number;
    cashEquivalent: number;
  }>;
}

// ---------- Snapshot shapes accepted by computeNilGoReadiness ------------
// Caller is the router; it knows how to pluck these from prisma. Keeping
// the input shape narrow and explicit prevents the service from getting
// entangled with the DB model.

export interface ReadinessInput {
  deal: {
    id: string;
    contractUrl: string | null;
    /** ISO date when the deal was signed; treated as "deal terms confirmed". */
    signedDate: string | null;
  };
  athlete: {
    ncaaId: string | null;
    eligibilityVerifiedAt: string | null;
  };
  payor: {
    id: string;
    legalName: string;
    affiliation: 'unknown' | 'associated' | 'non_associated';
    validationStatus: string; // verified | unable_to_verify | flagged | unverified | validating
  } | null;
  compensationItems: Array<{
    type: string;
    amount: number;
  }>;
  deliverables: Array<{
    activityCategory: ActivityCategory | null;
    metadata: Record<string, unknown> | string | null;
  }>;
  /** When known (US-033.3 will compute this), sum of all cash-equivalent
   * across this athlete × payor in the current calendar year. */
  payorAggregateExposure: number | null;
  /** Deals across every year (not just current calendar year) with this
   * payor that crossed the $600 threshold in their own year AND have no
   * live NilGoSubmission. Passed in by the router; the readiness service
   * just threads it through to the UI. Empty array when payor is unset
   * or nothing is owed. */
  unsubmittedReportableWithPayor?: ReadinessResult['unsubmittedReportableWithPayor'];
}

export function computeNilGoReadiness(input: ReadinessInput): ReadinessResult {
  const checks: ReadinessCheck[] = [];
  const dealHref = `/deals/${input.deal.id}`;

  // --- Payor checks ---------------------------------------------------
  if (!input.payor) {
    checks.push({
      id: 'payor_present',
      ok: false,
      message: "Pick the brand or business that's paying you (Payor).",
      fixHref: dealHref,
    });
    checks.push({
      id: 'payor_validated',
      ok: false,
      message: "We can't run business validation until a payor is picked.",
    });
    checks.push({
      id: 'payor_classified',
      ok: false,
      message: "Set the payor's NIL Go classification once it's picked.",
    });
  } else {
    checks.push({ id: 'payor_present', ok: true, message: 'Payor selected.' });
    // Validation: pass when the validator returned a definitive answer. We
    // do NOT block on "verified" — NIL Go itself accepts payors that we
    // couldn't independently confirm via Middesk/IRS; the athlete just
    // sees a softer status.
    const validationOk =
      input.payor.validationStatus === 'verified' ||
      input.payor.validationStatus === 'unable_to_verify' ||
      input.payor.validationStatus === 'flagged';
    checks.push({
      id: 'payor_validated',
      ok: validationOk,
      message: validationOk
        ? `Payor validation: ${input.payor.validationStatus.replace(/_/g, ' ')}.`
        : 'Run payor validation (Middesk/IRS) so NIL Go has the business profile.',
      fixHref: validationOk ? undefined : `/payors/${input.payor.id}`,
    });
    const classified = input.payor.affiliation !== 'unknown';
    checks.push({
      id: 'payor_classified',
      ok: classified,
      message: classified
        ? `Classified as ${input.payor.affiliation.replace(/_/g, '-')}.`
        : 'Set the payor classification (associated or non-associated entity).',
      fixHref: classified ? undefined : `/payors/${input.payor.id}`,
    });
  }

  // --- Compensation checks --------------------------------------------
  const total = input.compensationItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);
  const hasItems = input.compensationItems.length > 0;
  checks.push({
    id: 'compensation_present',
    ok: hasItems,
    message: hasItems
      ? `${input.compensationItems.length} compensation item${input.compensationItems.length === 1 ? '' : 's'} on file.`
      : 'Add at least one Compensation item (cash, goods/services, bonus, or royalty).',
    fixHref: hasItems ? undefined : dealHref,
  });
  checks.push({
    id: 'compensation_positive_total',
    ok: hasItems && total > 0,
    message:
      hasItems && total > 0
        ? `Cash-equivalent total: $${total.toLocaleString()}.`
        : 'At least one compensation item must have a cash-equivalent value greater than $0.',
    fixHref: hasItems && total > 0 ? undefined : dealHref,
  });

  // --- Activity checks ------------------------------------------------
  const hasDeliverables = input.deliverables.length > 0;
  checks.push({
    id: 'activity_present',
    ok: hasDeliverables,
    message: hasDeliverables
      ? `${input.deliverables.length} deliverable${input.deliverables.length === 1 ? '' : 's'} on file.`
      : 'Add at least one Activity / Deliverable (post, appearance, autograph, or endorsement).',
    fixHref: hasDeliverables ? undefined : dealHref,
  });
  const allDeliverablesDetailed =
    hasDeliverables &&
    input.deliverables.every((d) => {
      if (!d.activityCategory) return false;
      const schema = ACTIVITY_METADATA_SCHEMAS[d.activityCategory];
      const meta = normalizeMetadata(d.metadata);
      return schema.safeParse(meta).success;
    });
  checks.push({
    id: 'activity_detailed',
    ok: allDeliverablesDetailed,
    message: allDeliverablesDetailed
      ? 'Every deliverable has the category-specific detail NIL Go expects.'
      : 'One or more deliverables are missing required category-specific detail.',
    fixHref: allDeliverablesDetailed ? undefined : dealHref,
  });

  // --- Athlete eligibility checks -------------------------------------
  const hasNcaaId = !!input.athlete.ncaaId;
  checks.push({
    id: 'athlete_ncaa_id',
    ok: hasNcaaId,
    message: hasNcaaId ? 'NCAA ID on file.' : 'Add your NCAA Eligibility ID on your profile.',
    fixHref: hasNcaaId ? undefined : '/profile',
  });
  const verified = !!input.athlete.eligibilityVerifiedAt;
  checks.push({
    id: 'athlete_eligibility_verified',
    ok: verified,
    // Verification is NOT a strict requirement to submit — NIL Go can
    // accept a pending verification — but it shortens the review path
    // dramatically. We mark this `ok: false` (so it stays visible in
    // the checklist) but downgrade its weight in the status decision
    // (see allRequiredOk below).
    message: verified
      ? 'School verification complete.'
      : 'Your school has not verified your NCAA ID yet — submission is still allowed.',
    fixHref: verified ? undefined : '/profile',
  });

  // --- Deal terms / contract ------------------------------------------
  const termsUploaded = !!(input.deal.contractUrl || input.deal.signedDate);
  checks.push({
    id: 'deal_terms_uploaded',
    ok: termsUploaded,
    message: termsUploaded
      ? 'Deal terms on file (contract uploaded or signed date set).'
      : 'Upload the contract / signed terms (or set a signed date).',
    fixHref: termsUploaded ? undefined : dealHref,
  });

  // ---------- Final status decision -----------------------------------
  // Required for "ready": every check EXCEPT athlete_eligibility_verified
  // must pass. Eligibility-verified is informational — NIL Go does not
  // reject submissions for being unverified yet.
  const required = checks.filter((c) => c.id !== 'athlete_eligibility_verified');
  const allRequiredOk = required.every((c) => c.ok);

  // Reportability: under $600 deal total AND no historical aggregate
  // exposure of $600+ with this payor — neither alone is enough since the
  // aggregate rule sweeps unreported prior deals into the picture too.
  const aggUnderThreshold =
    input.payorAggregateExposure === null
      ? true
      : input.payorAggregateExposure < NIL_GO_THRESHOLD_USD;
  const reportable = total >= NIL_GO_THRESHOLD_USD || !aggUnderThreshold;

  // Priority: broken checks always surface as needs_attention regardless
  // of dollar amount — a half-built deal isn't "not reportable", it's
  // "needs work". Only when the checks all pass do we get to ask whether
  // this deal even needs to be reported.
  let status: ReadinessStatus;
  if (!allRequiredOk) {
    status = 'needs_attention';
  } else if (!reportable) {
    status = 'not_reportable';
  } else {
    status = 'ready';
  }

  return {
    status,
    checks,
    dealTotalCashEquivalent: total,
    payorAggregateExposure: input.payorAggregateExposure,
    unsubmittedReportableWithPayor: input.unsubmittedReportableWithPayor ?? [],
  };
}

function normalizeMetadata(
  metadata: Record<string, unknown> | string | null | undefined,
): Record<string, unknown> {
  if (metadata == null) return {};
  if (typeof metadata === 'string') {
    try {
      const parsed = JSON.parse(metadata);
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return metadata;
}
